import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { readSessionJson, removeSessionJson, writeSessionJson } from 'src/lib/sessionStorage';

/** Mirrors boulder-patient's ContactMethod enum. */
export enum ContactMethod {
  PHONE = 'phoneNumber',
  EMAIL = 'emailAddress',
}

/**
 * Field names mirror the PotentialParticipant model in boulder-patient
 * (src/features/registration/stores/registration.ts) so the two codebases stay consistent.
 */
export type RegistrationData = {
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  dob?: string;
  gender?: string;
  raceEthnicity: string[];
  pronouns?: string;
  phoneNumber?: string;
  email?: string;
  preferredIdentifier?: ContactMethod;
  communicationOptIn: boolean;
  consentToVoicemail: boolean;
};

export const DEFAULT_REGISTRATION_DATA: RegistrationData = {
  raceEthnicity: [],
  preferredIdentifier: ContactMethod.PHONE,
  communicationOptIn: true,
  consentToVoicemail: true,
};

const STORAGE_KEY = 'boulderRegistrationData';
const PROGRESS_STORAGE_KEY = 'boulderRegistrationProgress';

export enum RegistrationStep {
  ABOUT_YOU = 'aboutYou',
  CONTACT = 'contact',
  VERIFY = 'verify',
  CONFIRMED = 'confirmed',
}
export type RegistrationProgress = 0 | 1 | 2 | 3 | 4;

/**
 * 0 just means that no step has been completed
 * (on initial state, when there is no valid progress in session storage)
 */
export const completedProgressByStep: Record<RegistrationStep, RegistrationProgress> = {
  [RegistrationStep.ABOUT_YOU]: 1,
  [RegistrationStep.CONTACT]: 2,
  [RegistrationStep.VERIFY]: 3,
  [RegistrationStep.CONFIRMED]: 4,
};

const aboutYouFields: ReadonlySet<keyof RegistrationData> = new Set([
  'firstName',
  'lastName',
  'preferredName',
  'dob',
  'gender',
  'raceEthnicity',
  'pronouns',
]);

/**
 * Saved answers merged over the defaults, or undefined when there's nothing stored.
 *
 */
const readStoredRegistrationData = (): RegistrationData | undefined => {
  const stored = readSessionJson(STORAGE_KEY);

  return stored
    ? { ...DEFAULT_REGISTRATION_DATA, ...(stored as Partial<RegistrationData>) }
    : undefined;
};

const readStoredRegistrationProgress = (): RegistrationProgress => {
  const stored = readSessionJson(PROGRESS_STORAGE_KEY);

  return stored === 1 || stored === 2 || stored === 3 || stored === 4 ? stored : 0;
};

export type RegistrationDataContextValue = {
  registrationData: RegistrationData;
  registrationProgress: RegistrationProgress;
  /** True once the client has checked sessionStorage for answers from an earlier page load. */
  isHydrated: boolean;
  /** Curried to match formControl.js's `setValue(value)` contract. */
  setRegistrationField: <K extends keyof RegistrationData>(
    key: K,
  ) => (value: RegistrationData[K]) => void;
  /**
   * Discards every answer collected so far, in memory and in storage. Call this on successful
   * submission and from any explicit "start over" control.
   */
  clearRegistrationData: (options?: { preserveProgress?: boolean }) => void;
  completeRegistrationStep: (step: RegistrationStep) => void;
};

const context = createContext<RegistrationDataContextValue | undefined>(undefined);

/**
 * Mirrored into sessionStorage, so a refresh or a browser back/forward mid-flow doesn't discard
 * what the user has already typed. Mount from wrapRootElement, as wrapPageElement remounts on every
 * route change and would reset state between steps.
 *
 * sessionStorage, not localStorage, on purpose: this is identity and contact data on a possibly
 * shared device, and tab-scoped storage can't outlive the sitting. Don't swap it without a review.
 */
export const RegistrationDataProvider = ({ children }: { children: React.ReactNode }) => {
  /**
   * Always starts at the defaults, even when sessionStorage already holds data; the rehydrate
   * effect below restores them just after mount.
   *
   * Don't move that read into a lazy `useState` initializer. Gatsby builds this page's HTML with
   * empty fields, so reading storage here would make the first client render disagree with the
   * markup React is hydrating, and React deliberately leaves a hydrated input's DOM value alone,
   * so the fields would sit there visibly empty while state held the restored answers.
   */
  const [registrationData, setRegistrationData] =
    useState<RegistrationData>(DEFAULT_REGISTRATION_DATA);
  const [isHydrated, setIsHydrated] = useState(false);
  const [registrationProgress, setRegistrationProgress] = useState<RegistrationProgress>(0);

  const isFirstRun = useRef(true);

  /**
   * Restores saved answers on mount, then keeps storage in step with state on every change after
   * that. Storage is a mirror of state, so this is the only place that writes it.
   *
   * The first run must not write: it happens while state is still the defaults, and this provider
   * mounts on every page of the site, so writing here would drop an empty blob into the
   * sessionStorage of every visitor who never opens registration.
   */
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;

      const stored = readStoredRegistrationData();
      if (stored) {
        setRegistrationData(stored);
      }
      setRegistrationProgress(readStoredRegistrationProgress());
      setIsHydrated(true);
      return;
    }

    writeSessionJson(STORAGE_KEY, registrationData);
  }, [registrationData]);

  const setRegistrationField = useCallback(
    <K extends keyof RegistrationData>(key: K) =>
      (value: RegistrationData[K]) => {
        // A computed key off a generic widens to an index signature, hence the assertion.
        setRegistrationData((prev) => ({ ...prev, [key]: value } as RegistrationData));

        // Editing an earlier step means its submission (and every later one) is stale.
        setRegistrationProgress((current) => {
          const next = Math.min(current, aboutYouFields.has(key) ? 0 : 1) as RegistrationProgress;
          writeSessionJson(PROGRESS_STORAGE_KEY, next);
          return next;
        });
      },
    [],
  );

  const completeRegistrationStep = useCallback((step: RegistrationStep) => {
    const completed = completedProgressByStep[step];
    if (completed === undefined) {
      throw new Error(`Unknown registration step: ${String(step)}`);
    }
    setRegistrationProgress(completed);
    writeSessionJson(PROGRESS_STORAGE_KEY, completed);
  }, []);

  const clearRegistrationData = useCallback((options?: { preserveProgress?: boolean }) => {
    /**
     * Clears storage immediately rather than leaving it to the mirroring effect that runs in response
     * to state changes: a caller that navigates away or reloads immediately after calling this can't
     * wait for a render to commit.
     */
    removeSessionJson(STORAGE_KEY);
    setRegistrationData(DEFAULT_REGISTRATION_DATA);
    if (!options?.preserveProgress) {
      removeSessionJson(PROGRESS_STORAGE_KEY);
      setRegistrationProgress(0);
    }
  }, []);

  return (
    <context.Provider
      value={{
        registrationData,
        registrationProgress,
        isHydrated,
        setRegistrationField,
        clearRegistrationData,
        completeRegistrationStep,
      }}
    >
      {children}
    </context.Provider>
  );
};

export function useRegistrationData(): RegistrationDataContextValue {
  const value = useContext(context);

  if (!value) {
    throw new Error('useRegistrationData must be used inside <RegistrationDataProvider>.');
  }

  return value;
}
